const normalizeRiders = (selectedRiderIds = []) =>
  [...new Set((selectedRiderIds || []).map((id) => String(id)))]
    .sort();

const normalizeRiderCovers = (selectedRiderIds = [], riderCovers = {}) => {
  const covers = {};
  normalizeRiders(selectedRiderIds).forEach((id) => {
    const raw = riderCovers?.[id];
    if (raw == null || raw === '') return;
    const n = Number(raw);
    covers[id] = Number.isFinite(n) ? n : String(raw);
  });
  return covers;
};

/**
 * Stable key for premium contexts that materially affect quote results.
 */
export const buildPremiumContextKey = ({
  premiumFrequency,
  paymentTermType,
  selectedLimitedPayTerm,
  premiumRefundEnabled,
  enhancedCoverageEnabled,
  selectedRiderIds,
  riderCovers,
}) => {
  const isLP = paymentTermType === 'Limited Pay';
  return JSON.stringify({
    freq: String(premiumFrequency || 'Yearly'),
    termType: isLP ? 'LP' : 'RP',
    lpTerm: isLP ? Number(selectedLimitedPayTerm ?? 0) : 0,
    rop: Boolean(premiumRefundEnabled),
    ecbo: Boolean(enhancedCoverageEnabled),
    riders: normalizeRiders(selectedRiderIds),
    covers: normalizeRiderCovers(selectedRiderIds, riderCovers),
  });
};

export const getContextAwareMultiResult = (coverNode, policyTerm, contextKey) => {
  if (!coverNode || typeof coverNode !== 'object') return null;
  const ptKey = policyTerm != null ? String(policyTerm) : null;
  if (ptKey && contextKey) {
    const contextual = coverNode[`ctx:${contextKey}::${ptKey}`];
    if (contextual) return contextual;
  }
  if (ptKey && coverNode[ptKey]) return coverNode[ptKey];
  if (contextKey) {
    const contextualDefault = coverNode[`ctx:${contextKey}::_default`];
    if (contextualDefault) return contextualDefault;
  }
  return coverNode._default || null;
};

/**
 * Like getContextAwareMultiResult, but when contextKey is set never falls back to
 * unversioned coverNode[pt] (avoids stale premiums after ROP/ECBO/rider changes).
 */
export const getContextAwareMultiResultStrict = (
  coverNode,
  policyTerm,
  contextKey,
) => {
  if (!coverNode || typeof coverNode !== 'object') return null;
  const ptKey = policyTerm != null ? String(policyTerm) : null;
  if (contextKey) {
    if (ptKey) {
      const contextual = coverNode[`ctx:${contextKey}::${ptKey}`];
      if (contextual) return contextual;
    }
    const contextualDefault = coverNode[`ctx:${contextKey}::_default`];
    if (contextualDefault) return contextualDefault;
    return null;
  }
  return getContextAwareMultiResult(coverNode, policyTerm, contextKey);
};
